import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import dayjs from 'dayjs';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import useFetchMissingItems from 'pages/objectManager/hooks/useFetchMissingItems';
import { calendarClient } from 'services/calendarClient';
import { fetchFields } from 'slices/field.slice';
import {
  requestObjectTypes,
  selectObjectTypesList,
} from 'slices/objectTypes.slice';
import { fetchReservationModes } from 'slices/reservationMode.slice';
import { fetchReservationTemplates } from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { getErrorMessage } from 'utils/errorUtils';
import { errorNotification, successNotification } from 'utils/notifications';
import {
  type UpdateSyncSetting,
  reservationStatusesInput,
} from '@timeedit/calendar-integration-api-contract';
import type {
  CategoryColor,
  GeneralSetting,
  ObjectMapping,
  SyncSettingResponse,
} from '@timeedit/calendar-integration-api-contract';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { NEW_ITEM_ID } from '../shared';
import { CategoryColorSelector } from './CategoryColorSelector';
import { intlPrefix } from './MS365SyncSettingsDetails';
import MS365SyncSettingsDrawer from './MS365SyncSettingsDrawer';
import { MS365SyncSettingsList } from './MS365SyncSettingsList';
import './MS365SyncSettingsPage.scss';

type ReservationStatusInput = (typeof reservationStatusesInput)[number];
type SaveResponse = Awaited<
  ReturnType<
    | typeof calendarClient.syncSetting.create
    | typeof calendarClient.syncSetting.update
  >
>;

export function MS365SyncSettingsPage() {
  const dispatch = useAppDispatch();
  const [confirmDelete, deleteConfirmContextHolder] = useDeleteConfirmation();

  const [syncSetting, setSyncSetting] = useState<SyncSettingResponse | null>(
    null
  );
  const [secondSyncSetting, setSecondSyncSetting] =
    useState<SyncSettingResponse | null>(null);

  const [objectMapping, setObjectMapping] = useState<ObjectMapping | null>(
    null
  );
  const [syncSettings, setSyncSettings] = useState<SyncSettingResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [categoryColor, setCategoryColor] = useState<
    CategoryColor | null | undefined
  >(undefined);
  const [generalSetting, setGeneralSetting] = useState<GeneralSetting | null>(
    undefined
  );

  const objectTypes = useAppSelector(selectObjectTypesList);

  const mounted = useRef(true);

  const syncSettingMap = useMemo(
    () =>
      syncSettings.reduce<Record<string, SyncSettingResponse>>(
        (acc, syncSetting) => {
          acc[syncSetting.id] = syncSetting;
          return acc;
        },
        {}
      ),
    [syncSettings]
  );

  useEffect(() => {
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    dispatch(fetchReservationModes());
    dispatch(fetchReservationTemplates());
  }, []);

  const initObjectMapping = useCallback(async () => {
    try {
      const response = await calendarClient.objectMapping.getAll();
      if (response.status !== 200) {
        return errorNotification(getErrorMessage(response));
      }

      const objectMappings = response.body.results;
      setObjectMapping(objectMappings[0] ?? null);
    } catch (error) {
      errorNotification(getErrorMessage(error));
    }
  }, []);

  const initSyncSettings = useCallback(async () => {
    try {
      setLoading(true);

      const res = await calendarClient.syncSetting.getAll();

      setLoading(false);

      if (!mounted.current) {
        return;
      }

      if (res.status !== 200) {
        return errorNotification(getErrorMessage(res));
      }

      setSyncSettings(normalizeTimezones(res.body.results));
    } catch (error) {
      setLoading(false);
      errorNotification(getErrorMessage(error));
    }
  }, []);

  const getGeneralSettings = useCallback(async () => {
    try {
      const res = await calendarClient.generalSetting.get();

      if (res.status !== 200) {
        return errorNotification(getErrorMessage(res));
      }

      setGeneralSetting(res.body);
      setCategoryColor(res.body.categoryColor);
    } catch (error) {
      errorNotification(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    void getGeneralSettings();
    void initObjectMapping();
    void initSyncSettings();

    return () => {
      mounted.current = false;
    };
  }, [initObjectMapping, initSyncSettings, getGeneralSettings]);

  const selectedIds = useSelectResource(
    'objectId',
    syncSettingMap,
    syncSetting,
    secondSyncSetting,
    setSyncSetting,
    setSecondSyncSetting
  );

  useFetchMissingItems<SyncSettingResponse, string>({
    itemIds: selectedIds.filter((id) => id !== NEW_ITEM_ID),
    itemMap: syncSettingMap,
    fetchItemsFn: initSyncSettings,
  });

  const onRowClick = useCallback((config: SyncSettingResponse) => {
    setSyncSetting(config);
  }, []);

  const highlightedIds = useMemo(
    () => [syncSetting?.id, secondSyncSetting?.id].filter(Boolean),
    [secondSyncSetting?.id, syncSetting?.id]
  );

  const onRow = useRowProps(onRowClick, highlightedIds);

  const onClose = useCallback((syncSetting: SyncSettingResponse) => {
    setSyncSetting((prev) => (prev?.id === syncSetting.id ? null : prev));
    setSecondSyncSetting((prev) => (prev?.id === syncSetting.id ? null : prev));
  }, []);

  const onCreate = useCallback(() => {
    const dateInOneWeek = dayjs().add(7, 'days').format('YYYY-MM-DD');
    const dateInTwoWeeks = dayjs().add(14, 'days').format('YYYY-MM-DD');

    const initSyncType = (() => {
      if (objectMapping?.room?.objectTypeId) {
        return 'ROOM';
      } else if (objectMapping?.staff?.objectTypeId) {
        return 'STAFF';
      } else if (objectMapping?.student?.objectTypeId) {
        return 'STUDENT';
      }
    })();

    setSyncSetting({
      id: NEW_ITEM_ID,
      name: '',
      syncType: initSyncType,
      syncPeriod: {
        type: 'STATIC',
        startDate: dateInOneWeek,
        endDate: dateInTwoWeeks,
      },
      syncMode: {
        mode: 'TE_OUT',
        subjectTemplates: [],
        locationTemplates: [],
        descriptionFields: [],
        reminder: { behavior: 'USE_DEFAULT' },
      },
      syncTarget: 'ALL',
      objectIds: [],
    });
  }, [objectMapping]);

  const deleteSyncSetting = useCallback(
    async (syncSetting: SyncSettingResponse) => {
      try {
        const { id } = syncSetting;

        const response = await calendarClient.syncSetting.delete({
          params: { id },
        });

        if (response.status < 200 || response.status >= 300) {
          return errorNotification(getErrorMessage(response));
        }

        setSyncSettings((prevConfigs) => {
          const deletedIndex = prevConfigs.findIndex((c) => c.id === id);
          if (deletedIndex === -1) {
            return prevConfigs;
          }

          const newConfigs = prevConfigs.slice();
          newConfigs.splice(deletedIndex, 1);
          return newConfigs;
        });
        onClose(syncSetting);
      } catch (error) {
        errorNotification(getErrorMessage(error));
      }
    },
    [onClose]
  );

  const onDelete = useCallback(
    (syncSetting: SyncSettingResponse) => {
      confirmDelete(syncSetting.name, () => deleteSyncSetting(syncSetting));
    },
    [confirmDelete, deleteSyncSetting]
  );

  const onSave = useCallback(
    (syncSetting: SyncSettingResponse) => {
      const { id, ...body } = syncSetting;

      const syncMode =
        body.syncMode.mode === 'TE_OUT' || body.syncMode.mode === 'TE_TWO_WAY'
          ? {
              ...body.syncMode,
              reservationStatuses: body.syncMode.reservationStatuses?.filter(
                (s): s is ReservationStatusInput =>
                  reservationStatusesInput.includes(s as ReservationStatusInput)
              ),
            }
          : body.syncMode;

      const validStatusesBody = {
        ...body,
        syncMode,
      };

      const savePromise =
        id === NEW_ITEM_ID
          ? calendarClient.syncSetting.create({
              body: validStatusesBody,
            })
          : calendarClient.syncSetting.update({
              params: { id },
              body: validStatusesBody,
            });

      savePromise
        .then((response: SaveResponse) => {
          if (response.status >= 200 && response.status < 300) {
            initSyncSettings();
            onClose(syncSetting);
          } else {
            errorNotification(getErrorMessage(response));
          }
        })
        .catch((err) => {
          errorNotification(err.message);
        });
    },
    [initSyncSettings, onClose]
  );

  const onCompare = useCallback((syncSetting: SyncSettingResponse) => {
    if (syncSetting) {
      setSecondSyncSetting(syncSetting);
    } else {
      setSyncSetting(syncSetting);
    }
  }, []);

  const onDuplicate = useCallback((syncSetting: SyncSettingResponse) => {
    setSyncSetting({
      ...syncSetting,
      id: NEW_ITEM_ID,
      name: `${syncSetting.name} (new)`,
    });
  }, []);

  const checkDrawerOpen = useCallback(
    () => Boolean(syncSetting),
    [syncSetting]
  );

  const menuProps = useCallback(
    (record: SyncSettingResponse) =>
      getMenuProps({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [checkDrawerOpen, onCompare, onDelete, onDuplicate]
  );

  const syncSettingsWithKey = useMemo(() => {
    return syncSettings.map((syncSetting) => ({
      ...syncSetting,
      key: syncSetting.id,
    }));
  }, [syncSettings]);

  const discardCategoryColorChanges = useCallback(() => {
    setCategoryColor(generalSetting?.categoryColor);
  }, [generalSetting]);

  const saveCategoryColorChanges = useCallback(async () => {
    if (!generalSetting) {
      return;
    }

    try {
      const res = await calendarClient.generalSetting.update({
        body: { categoryColor },
      });

      if (res.status !== 200) {
        return errorNotification(getErrorMessage(res));
      }

      setGeneralSetting(res.body);
      successNotification(
        getText(`${intlPrefix}.category_color.save_successful`)
      );
    } catch (error) {
      errorNotification(getErrorMessage(error));
    }
  }, [categoryColor, generalSetting]);

  return (
    <DefaultContentLayout
      title={getText(`${intlPrefix}.title`)}
      subtitle={getText(`${intlPrefix}.subtitle`)}
      actions={[
        <Button
          key="discard"
          disabled={categoryColor === generalSetting?.categoryColor}
          onClick={discardCategoryColorChanges}
        >
          {getText(`${intlPrefix}.actions.discard_changes`)}
        </Button>,
        <Button
          key="save"
          type="primary"
          disabled={categoryColor === generalSetting?.categoryColor}
          onClick={saveCategoryColorChanges}
        >
          {getText(`${intlPrefix}.actions.save_changes`)}
        </Button>,
      ]}
    >
      {deleteConfirmContextHolder}
      <div className="ms365-sync-settings-page inner-content--wrapper">
        <LoadingWrapper isLoading={loading}>
          <div style={{ maxWidth: 500, marginBottom: 20, marginTop: 15 }}>
            <CategoryColorSelector
              value={categoryColor}
              onChange={setCategoryColor}
            />
          </div>
          <MS365SyncSettingsList
            dataSource={syncSettingsWithKey}
            getMenuProps={menuProps}
            onCreate={onCreate}
            onRow={onRow}
            createDisabled={
              !objectMapping?.room?.objectTypeId &&
              !objectMapping?.staff?.objectTypeId &&
              !objectMapping?.student?.objectTypeId
            }
          />
        </LoadingWrapper>

        <MS365SyncSettingsDrawer
          objectMapping={objectMapping}
          syncSetting={syncSetting}
          secondSyncSetting={secondSyncSetting}
          onClose={onClose}
          onDelete={onDelete}
          onDiscard={onClose}
          onSave={onSave}
          objectTypes={objectTypes}
        />
      </div>
    </DefaultContentLayout>
  );
}

function normalizeTimezones(
  syncSettings: SyncSettingResponse[]
): SyncSettingResponse[] {
  return syncSettings.map((syncSetting) => {
    const syncPeriod = syncSetting.syncPeriod;
    if (syncPeriod.type === 'DYNAMIC') {
      return syncSetting;
    }

    return {
      ...syncSetting,
      syncPeriod: {
        ...syncPeriod,
        startDate: dayjs(syncPeriod.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(syncPeriod.endDate).format('YYYY-MM-DD'),
      },
    };
  });
}
